import { AlertCircle, Inbox, RotateCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';
import { Button } from '../ui/button.js';

/** Diagonal yellow/black hazard stripe — the Zuruflex divider, used sparingly. */
export function StripeDivider({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('h-1.5 w-full rounded-pill', className)}
      style={{
        backgroundImage: 'repeating-linear-gradient(-45deg, #FFE500 0 10px, #0A0A0A 10px 20px)',
      }}
    />
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-card bg-surface-sunken px-6 py-16 text-center',
        className,
      )}
    >
      <div className="flex size-12 items-center justify-center rounded-full bg-accent-soft text-accent-soft-text">
        {icon ?? <Inbox className="size-5" />}
      </div>
      <h3 className="font-display text-lg font-bold">{title}</h3>
      {description ? <p className="max-w-sm text-sm text-text-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const { t } = useTranslation('common');
  const message = error instanceof Error ? error.message : t('error.title');

  return (
    <div className={cn('flex gap-3 rounded-card bg-danger/8 p-5', className)}>
      <AlertCircle className="mt-0.5 size-5 shrink-0 text-danger" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-bold text-danger">{t('error.requestFailed')}</p>
        <p className="mt-1 text-sm text-text-muted">{message}</p>
        {onRetry ? (
          <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
            <RotateCw />
            {t('action.retry')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
